import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { VoiceManager, PeerChangeInfo, VoiceError } from '../lib/voice-manager';

/**
 * Mensajes de voz en español por código: el usuario tiene que entender qué
 * falló sin leer el detalle técnico.
 */
const VOICE_ERROR_MESSAGES: Record<string, string> = {
  start_failed: 'No se pudo activar el micrófono. Revisá los permisos del navegador.',
  consume_failed: 'No se pudo conectar con el audio de otro jugador.',
  consume_setup_failed: 'No se pudo preparar la recepción de audio.',
  rtp_caps_failed: 'El servidor no pudo negociar el audio.',
  audio_play_failed: 'No se pudo reproducir el audio remoto.',
};

const describeVoiceError = (err: VoiceError): string =>
  VOICE_ERROR_MESSAGES[err.code] ?? `Error de voz: ${err.message || err.code}`;

@customElement('jgz-voice-controls')
export class JgzVoiceControls extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ attribute: false }) voiceManager: VoiceManager | null = null;
  @state() private enabledPeerIds = new Set<string>();
  @state() private localEnabled = false;
  @state() private localMuted = true;
  /** Último error de voz, para mostrarlo al usuario junto al botón. */
  @state() private errorMessage: string | null = null;

  private peerUnsub: (() => void) | null = null;
  private stateUnsub: (() => void) | null = null;
  private errorUnsub: (() => void) | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.syncFromManager();
    if (this.voiceManager) {
      this.subscribePeer();
      this.subscribeState();
      this.subscribeError();
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.peerUnsub?.();
    this.peerUnsub = null;
    this.stateUnsub?.();
    this.stateUnsub = null;
    this.errorUnsub?.();
    this.errorUnsub = null;
  }

  willUpdate(changed: Map<string, unknown>): void {
    if (changed.has('voiceManager')) {
      this.syncFromManager();
      if (this.voiceManager) {
        this.subscribePeer();
        this.subscribeState();
        this.subscribeError();
        if (this.voiceManager.consumeAutoStartOnMount()) {
          this.voiceManager.start().catch(() => {});
        }
      } else {
        this.peerUnsub?.();
        this.peerUnsub = null;
        this.stateUnsub?.();
        this.stateUnsub = null;
        this.errorUnsub?.();
        this.errorUnsub = null;
      }
    }
  }

  private syncFromManager(): void {
    if (!this.voiceManager) {
      this.localEnabled = false;
      this.localMuted = true;
      return;
    }
    this.localEnabled = this.voiceManager.isEnabled();
    this.localMuted = !this.voiceManager.isMicActive();
  }

  private subscribePeer(): void {
    if (!this.voiceManager) return;
    this.peerUnsub = this.voiceManager.onPeerChange((info) => this.onPeer(info));
  }

  private subscribeState(): void {
    if (!this.voiceManager) return;
    this.stateUnsub = this.voiceManager.onStateChange(() => this.syncFromManager());
  }

  /**
   * Conecta el canal de errores del `VoiceManager`: sin esto los fallos de
   * voz (permisos, negociación, reproducción) solo llegaban al console.
   */
  private subscribeError(): void {
    this.errorUnsub?.();
    this.errorUnsub = null;
    if (!this.voiceManager) return;
    this.errorUnsub = this.voiceManager.onError((err) => this.onVoiceError(err));
  }

  private onVoiceError(err: VoiceError): void {
    this.errorMessage = describeVoiceError(err);
  }

  private onPeer(info: PeerChangeInfo): void {
    if (info.enabled) this.enabledPeerIds.add(info.playerId);
    else this.enabledPeerIds.delete(info.playerId);
    this.requestUpdate();
    this.dispatchEvent(
      new CustomEvent('jgz-voice-peer-count', {
        detail: { count: this.enabledPeerIds.size },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private onClick = (e: Event): void => {
    e.stopPropagation();
    if (!this.voiceManager) return;
    // Al reintentar se limpia el error anterior.
    this.errorMessage = null;
    if (!this.localEnabled) {
      const p = this.voiceManager.start();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {});
      }
    } else if (!this.localMuted) {
      this.voiceManager.setMuted(true);
    } else {
      this.voiceManager.setMuted(false);
    }
  };

  render() {
    if (!this.voiceManager) return nothing;
    const micActive = this.localEnabled && !this.localMuted;
    const state = !this.localEnabled ? 'off' : micActive ? 'on' : 'muted';
    const label = !this.localEnabled
      ? 'Activar micrófono'
      : micActive
      ? 'Silenciar micrófono'
      : 'Reactivar micrófono';
    const showSlash = state === 'muted';
    return html`
      <button
        type="button"
        class="voice-mic-icon voice-mic-${state}"
        @click=${this.onClick}
        aria-label=${label}
        aria-pressed=${micActive ? 'true' : 'false'}
        data-testid="voice-toggle"
        data-state=${state}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"></path>
          <path d="M19 10v1a7 7 0 0 1-14 0v-1"></path>
          <line x1="12" y1="19" x2="12" y2="22"></line>
          ${showSlash ? html`<line x1="2" y1="2" x2="22" y2="22"></line>` : nothing}
        </svg>
      </button>
      ${this.errorMessage
        ? html`<span
            class="voice-error"
            role="alert"
            data-testid="voice-error"
            style="display: inline-block; max-width: 16rem; font-size: 0.72rem; line-height: 1.2; color: var(--orange-2);"
          >
            ⚠️ ${this.errorMessage}
          </span>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'jgz-voice-controls': JgzVoiceControls;
  }
}